import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isStudent: boolean;
  login: (identifier: string, password: string, loginMethod?: 'email' | 'userid' | 'phone') => Promise<any>;
  register: (dataOrName: FormData | string, email?: string, password?: string) => Promise<{ message: string }>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount
    const currentUser = authService.getCurrentUser();
    if (currentUser && authService.isAuthenticated()) {
      if (process.env.NODE_ENV === 'development') {
        // console.log('Found authenticated user on startup:', currentUser);
      }
      setUser(currentUser);
    }
    setIsLoading(false);
  }, []);

  const login = async (identifier: string, password: string, loginMethod: 'email' | 'userid' | 'phone' = 'email') => {
    console.log('Login attempt for:', identifier, 'Method:', loginMethod);
    const response = await authService.login(identifier, password, loginMethod);
    console.log('Login successful, setting user:', response.user);
    setUser(response.user);
    return response;
  };

  const register = async (dataOrName: FormData | string, email?: string, password?: string) => {
    console.log('Register attempt');
    
    if (dataOrName instanceof FormData) {
      // Handle FormData (new enhanced registration)
      console.log('Registering with FormData');
      const response = await authService.registerWithFormData(dataOrName);
      return { message: response.message };
    } else {
      // Handle traditional parameters (backward compatibility)
      console.log('Register attempt for:', email);
      const response = await authService.register(dataOrName, email!, password!);
      return { message: response.message };
    }
  };

  const logout = async () => {
    console.log('Logout initiated');
    await authService.logout();
    setUser(null);
    console.log('User logged out, user state cleared');
  };

  const updateUser = (updatedUser: User) => {
    console.log('Updating user:', updatedUser);
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isStudent: user?.role === 'student',
    login,
    register,
    logout,
    updateUser,
  };

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 